import { cn } from '@/utils/utils.ts';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Ellipsis, User } from 'lucide-react';
import { PlantMenuPopoverChangeViewButton } from '@/features/plants/components/plant-overview/plant-menu-popover-change-view-button.tsx';
import { PlantMenuPopoverGenerateReportDialog } from '@/features/plants/components/plant-overview/plant-menu-popover-generate-report-dialog.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Link, useParams, useLocation } from 'react-router';
import { PlantUserAccessManagementDialog } from '@/features/plants/components/plant-management/user-access/plant-user-access-management.tsx';
import { usePermissions } from '@/hooks/use-permission.ts';
import { useGetPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant.ts';
import { usePlantsStore } from '@/services/store/use-plants-store.tsx';
import { useShallow } from 'zustand/react/shallow';

export function PlantMenuPopover({ isLoading }: { isLoading: boolean }) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { plantId } = useParams();
  const location = useLocation();

  const { data: plant } = useGetPlantQuery({ plantId });

  const setPageView = usePlantsStore(useShallow((state) => state.setPageView));

  const isOnPlantPage = location.pathname === `/plants/${plantId}`;

  const [
    canViewControlPanel,
    canViewSchedule,
    canViewEdit,
    canViewConnectionDetails,
    canViewAccessManagement,
    canViewGenerateReport,
  ] = usePermissions<'plants'>([
    ['plants', 'view-page.control-panel'],
    ['plants', 'view-page.schedule'],
    ['plants', 'view-page.edit'],
    ['plants', 'view-page.connection-details'],
    ['plants', 'view-page.user-access-management'],
    ['plants', 'view-generate-report.plant'],
  ]);

  const menuItems = useMemo(
    () => [
      {
        label: 'Control Panel',
        to: `/plants/${plantId}/control-panel`,
        canView: canViewControlPanel,
      },
      {
        label: 'Schedule',
        to: `/plants/${plantId}/schedule`,
        canView: canViewSchedule,
      },
      {
        label: 'Edit Plant',
        to: `/edit-plant/${plantId}`,
        canView: canViewEdit,
      },
      {
        label: 'View Connection Details',
        to: `/plants/${plantId}/connection-details`,
        canView: canViewConnectionDetails,
      },
    ],
    [
      canViewControlPanel,
      canViewSchedule,
      canViewEdit,
      canViewConnectionDetails,
      plantId,
      plant?.plantId,
    ]
  );

  if (isLoading) {
    return <Skeleton className="h-9 w-12" />;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-[48px] border-2 border-gray-300 p-2">
          <Ellipsis />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'relative mr-4 w-55 overflow-hidden rounded-lg border bg-white/95 p-1 shadow-xl backdrop-blur-xl dark:bg-gray-900/95'
        )}
      >
        {plant && (
          <div>
            {isOnPlantPage ? (
              <PlantMenuPopoverChangeViewButton />
            ) : (
              <Link
                to={`/plants/${plantId}`}
                onClick={() => setPageView('advanced')}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
                  'text-left',
                  'hover:bg-accent hover:text-accent-foreground',
                  'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
                  'disabled:pointer-events-none disabled:opacity-50'
                )}
              >
                <User className="size-4" />
                Overview
              </Link>
            )}
            {canViewGenerateReport && <PlantMenuPopoverGenerateReportDialog />}
            <div role="separator" className="bg-muted -mx-1 my-1 h-px"></div>

            {menuItems.map((item) =>
              item.canView ? (
                <Link
                  key={item.label}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
                    'text-left',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
                    'disabled:pointer-events-none disabled:opacity-50'
                  )}
                  to={item.to}
                >
                  <span className="size-4" />
                  {item.label}
                </Link>
              ) : null
            )}

            {canViewAccessManagement && (
              <PlantUserAccessManagementDialog plant={plant}>
                <button
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
                    'text-left',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
                    'disabled:pointer-events-none disabled:opacity-50'
                  )}
                  role="menuitem"
                >
                  <span className="size-4" />
                  User Access Management
                </button>
              </PlantUserAccessManagementDialog>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
